import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, Chip, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DownloadIcon } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { getReferralFees, getMyReferralFees } from '../../api/commissionProgressApi';
import { handlePriceWithRM, formatCompactRm } from '../../utils/priceUtils';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';

dayjs.extend(quarterOfYear);

/**
 * Referral Report — the same shape as the Commission Report, for referral fees.
 * (user 2026-10-14: "can i have a referral report aso ... can do similar like commission
 * page just use it as referral")
 *
 * One list of REFERRERS with their totals, and one individual report per referrer. Both are
 * built from GetReferralFees — the same single derivation the All RF list and the referral
 * detail page use, so the four screens cannot disagree about what a fee is.
 *
 * Why it is not the Commission Report with a filter: an Astern-borne fee has no
 * CommissionProgress row at all, so it is invisible to every commission query by design.
 * The Commission Report would show the rare agent-borne case and silently omit the norm.
 */

const FONT = "'Poppins', sans-serif";
const BORNE_COLORS = { Astern: '#0E8FD1', Agent: '#F59E0B' };

const DATE_PRESETS = [
    { label: 'All Time', start: () => null, end: () => null },
    { label: 'This Month', start: () => dayjs().startOf('month'), end: () => dayjs() },
    { label: 'Last Month', start: () => dayjs().subtract(1, 'month').startOf('month'), end: () => dayjs().subtract(1, 'month').endOf('month') },
    { label: 'This Quarter', start: () => dayjs().startOf('quarter'), end: () => dayjs() },
    { label: 'This Year', start: () => dayjs().startOf('year'), end: () => dayjs() },
];

// ── One referral row, flattened ────────────────────────────────────────────────
// Everything downstream reads THIS shape, so the awkward two-object API response is
// unpacked exactly once.
function flatten(r) {
    const d = r.salesDetailsForCommissionProgress || {};
    const cp = r.commissionProgressDto || {};
    const fee = Number(cp.commPay) || 0;
    const paid = (Number(cp.firstPayment) || 0) + (Number(cp.secondPayment) || 0);
    return {
        salesId: d.salesId,
        referrerId: d.referrerUserId ?? cp.paymentTo ?? 0,
        referrerName: d.referrerName || cp.paymentToName || '(unnamed)',
        borneBy: d.referralPaidBy === 'Agent' ? 'Agent' : 'Astern',
        ratePct: d.referralRatePct,
        fee,
        paid,
        // Outstanding is what is still owed on a fee that is DUE. It is never negative:
        // an overpayment is a different problem and must not quietly cancel out someone
        // else's unpaid fee in a total.
        outstanding: Math.max(0, fee - paid),
        customerName: d.customerName,
        agentName: d.agentName,
        coAgentName: d.coAgentName,
        finalPrice: Number(d.finalPrice) || 0,
        depositOn: d.depositOn,
        status: d.status,
        isArchived: !!d.isArchived,
        custPaid: Number(d.paidTotal) || 0,
        custTotal: Number(d.effectiveTotal) || 0,
        referrerIsCustomer: !!d.referrerIsCustomer,
        bankName: d.referrerBankName,
        bankAccount: d.referrerBankAccountNumber,
        bankBeneficiary: d.referrerBankBeneficiaryName,
        phone: d.referrerPhone,
        email: d.referrerEmail,
    };
}

// Archived sales are out of the book entirely, so they are out of the report too — the
// same rule the Commission Report applies. Status is NOT filtered here: a referral on a
// Pending sale is a real commitment the referrer is waiting on, and hiding it would make
// the report disagree with the All RF list it is read beside. The Status column says so.
const liveOnly = (rows) => rows.filter(r => !r.isArchived);

const inRange = (r, start, end) => {
    if (!start && !end) return true;
    if (!r.depositOn) return false;
    const d = dayjs(r.depositOn);
    if (start && d.isBefore(dayjs(start).startOf('day'))) return false;
    if (end && d.isAfter(dayjs(end).endOf('day'))) return false;
    return true;
};

const downloadCsv = (filename, rows) => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const esc = (v) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => esc(r[h])).join(','))].join('\n');
    const a = document.createElement('a');
    // BOM so Excel opens the Malay/Chinese names correctly rather than as mojibake.
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
};

// ── Shared chrome ──────────────────────────────────────────────────────────────
function Kpi({ label, value, sub, color = '#0F172A' }) {
    return (
        <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '14px' }}>
            <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontFamily: FONT }}>{label}</Typography>
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color, fontFamily: FONT }}>{value}</Typography>
                {sub && <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontFamily: FONT }}>{sub}</Typography>}
            </CardContent>
        </Card>
    );
}

function DateFilter({ preset, setPreset, startDate, setStartDate, endDate, setEndDate }) {
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            {DATE_PRESETS.map(p => (
                <Chip
                    key={p.label}
                    label={p.label}
                    size="small"
                    onClick={() => { setPreset(p.label); setStartDate(p.start()); setEndDate(p.end()); }}
                    sx={{
                        fontFamily: FONT, fontWeight: 600, fontSize: '0.72rem',
                        bgcolor: preset === p.label ? '#1A8CFF' : '#F1F5F9',
                        color: preset === p.label ? '#FFFFFF' : '#475569',
                        '&:hover': { bgcolor: preset === p.label ? '#1A8CFF' : '#E2E8F0' },
                    }} />
            ))}
            <DatePicker label="From" value={startDate} onChange={(v) => { setStartDate(v); setPreset('Custom'); }}
                slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
            <DatePicker label="To" value={endDate} onChange={(v) => { setEndDate(v); setPreset('Custom'); }}
                slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
        </Box>
    );
}

const th = {
    backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase',
    fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.4px', color: '#64748B',
    fontFamily: FONT, padding: '8px 10px', whiteSpace: 'nowrap',
};
const td = { py: 0.75, px: 1.25, fontFamily: FONT, fontSize: '0.8rem', color: '#374151' };

function PayChip({ fee, paid }) {
    const done = paid > 0 && paid >= fee;
    const part = paid > 0 && paid < fee;
    return (
        <Chip size="small" label={done ? 'Paid' : part ? 'Part paid' : 'Not paid'}
            sx={{
                height: 20, fontSize: '0.66rem', fontWeight: 700,
                bgcolor: done ? '#F0FDF4' : part ? '#FEF3C7' : '#F1F5F9',
                color: done ? '#166534' : part ? '#92400E' : '#475569',
            }} />
    );
}

// `mine` picks the agent-scoped endpoint: referrals on sales the caller sold, with the
// referrer's bank details stripped server-side. The whole list stays behind AllSalesCommList.
// (user 2026-10-14)
function useReferralRows(mine = false) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        let cancelled = false;
        (mine ? getMyReferralFees() : getReferralFees())
            .then(res => { if (!cancelled) setRows(liveOnly((res.data || []).map(flatten))); })
            .catch(e => { console.error('Failed to load referral fees', e); if (!cancelled) setFailed(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [mine]);
    return { rows, loading, failed };
}

// ── 1. The report: every referrer, with their totals ───────────────────────────
// `mine` renders the agent's own slice at /reports/referral/mine — same page, narrower data.
export default function ReferralReport({ mine = false }) {
    const navigate = useNavigate();
    const { rows, loading, failed } = useReferralRows(mine);
    const [preset, setPreset] = useState('All Time');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const filtered = useMemo(() => rows.filter(r => inRange(r, startDate, endDate)), [rows, startDate, endDate]);

    const byReferrer = useMemo(() => {
        const m = new Map();
        for (const r of filtered) {
            const key = r.referrerId || r.referrerName;
            if (!m.has(key)) {
                m.set(key, {
                    referrerId: r.referrerId, referrerName: r.referrerName,
                    phone: r.phone, email: r.email,
                    bankName: r.bankName, bankAccount: r.bankAccount,
                    count: 0, fee: 0, paid: 0, outstanding: 0, revenue: 0,
                    astern: 0, agent: 0, rates: new Set(),
                });
            }
            const g = m.get(key);
            g.count += 1;
            g.fee += r.fee;
            g.paid += r.paid;
            g.outstanding += r.outstanding;
            g.revenue += r.finalPrice;
            if (r.borneBy === 'Agent') g.agent += 1; else g.astern += 1;
            if (r.ratePct != null) g.rates.add(Number(r.ratePct));
        }
        return [...m.values()].sort((a, b) => b.fee - a.fee);
    }, [filtered]);

    const totals = useMemo(() => ({
        referrers: byReferrer.length,
        referrals: filtered.length,
        fee: filtered.reduce((s, r) => s + r.fee, 0),
        paid: filtered.reduce((s, r) => s + r.paid, 0),
        outstanding: filtered.reduce((s, r) => s + r.outstanding, 0),
        revenue: filtered.reduce((s, r) => s + r.finalPrice, 0),
        asternFee: filtered.filter(r => r.borneBy === 'Astern').reduce((s, r) => s + r.fee, 0),
        agentFee: filtered.filter(r => r.borneBy === 'Agent').reduce((s, r) => s + r.fee, 0),
        noBank: byReferrer.filter(g => !g.bankName && !g.bankAccount).length,
    }), [filtered, byReferrer]);

    const monthly = useMemo(() => {
        const m = new Map();
        for (const r of filtered) {
            if (!r.depositOn) continue;
            const k = dayjs(r.depositOn).format('YYYY-MM');
            m.set(k, (m.get(k) || 0) + r.fee);
        }
        return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
            .map(([k, v]) => ({ month: dayjs(k + '-01').format('MMM YY'), fee: Math.round(v) }));
    }, [filtered]);

    const exportAll = () => downloadCsv(
        `Referral_Report_${dayjs().format('YYYYMMDD')}.csv`,
        byReferrer.map(g => ({
            Referrer: g.referrerName,
            'Referrer ID': g.referrerId,
            Phone: g.phone ?? '',
            Email: g.email ?? '',
            // Nothing to export in the agent view — the server never sent it.
            ...(mine ? {} : { Bank: g.bankName ?? '', 'Bank Account No': g.bankAccount ?? '' }),
            Referrals: g.count,
            'Rate(s) %': [...g.rates].sort((a, b) => a - b).join(' / '),
            'Sales Value': g.revenue,
            'Fee Due': g.fee,
            'Fee Paid': g.paid,
            Outstanding: g.outstanding,
            'Borne by Astern': g.astern,
            'Borne by Agent': g.agent,
        })));

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    }

    return (
        <div>
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>
                    {mine ? 'My Referral Report' : 'Referral Report'}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: FONT }}>
                    {mine
                        ? 'Referrals on sales you sold, as agent or co-broke. Click a referrer for their individual report.'
                        : 'Every referrer and what they are owed. Click a referrer for their individual report.'}
                </Typography>
            </Box>

            {failed && <Alert severity="error" sx={{ mb: 2, fontFamily: FONT }}>Could not load referral fees.</Alert>}

            <DateFilter {...{ preset, setPreset, startDate, setStartDate, endDate, setEndDate }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2 }}>
                <Kpi label="Referrers" value={totals.referrers} sub={`${totals.referrals} referral${totals.referrals === 1 ? '' : 's'}`} />
                <Kpi label="Fee Due" value={handlePriceWithRM(totals.fee)} sub={`on ${formatCompactRm(totals.revenue)} of sales`} />
                <Kpi label="Paid" value={handlePriceWithRM(totals.paid)} color="#166534" />
                <Kpi label="Outstanding" value={handlePriceWithRM(totals.outstanding)} color={totals.outstanding > 0 ? '#B45309' : '#0F172A'} />
                {/* Bank details never reach an agent, so counting the missing ones would read
                    as "every referrer is unpayable". Sales value in its place. */}
                {mine
                    ? <Kpi label="Sales Value" value={handlePriceWithRM(totals.revenue)} />
                    : <Kpi label="No Bank Details" value={totals.noBank} color={totals.noBank > 0 ? '#B91C1C' : '#0F172A'}
                           sub={totals.noBank > 0 ? 'cannot be paid out' : 'all payable'} />}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 1.5, mb: 2 }}>
                <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '14px' }}>
                    <CardContent>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, fontFamily: FONT, mb: 1 }}>Referral fees by month</Typography>
                        {monthly.length === 0 ? (
                            <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', fontFamily: FONT }}>Nothing in this period.</Typography>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={monthly}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: FONT }} />
                                    <YAxis tick={{ fontSize: 11, fontFamily: FONT }} tickFormatter={formatCompactRm} />
                                    <Tooltip formatter={(v) => handlePriceWithRM(v)} />
                                    <Bar dataKey="fee" fill="#0E8FD1" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
                <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '14px' }}>
                    <CardContent>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, fontFamily: FONT, mb: 1 }}>Who bears the fee</Typography>
                        {totals.fee === 0 ? (
                            <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', fontFamily: FONT }}>Nothing in this period.</Typography>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie
                                        data={[
                                            { name: 'Astern', value: Math.round(totals.asternFee) },
                                            { name: 'Agent', value: Math.round(totals.agentFee) },
                                        ].filter(x => x.value > 0)}
                                        dataKey="value" nameKey="name" outerRadius={70} label>
                                        {['Astern', 'Agent'].map(k => <Cell key={k} fill={BORNE_COLORS[k]} />)}
                                    </Pie>
                                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: FONT }} />
                                    <Tooltip formatter={(v) => handlePriceWithRM(v)} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
            </Box>

            <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '14px' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, fontFamily: FONT }}>Referrers</Typography>
                    <Button size="small" startIcon={<DownloadIcon size={14} />} onClick={exportAll}
                        sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
                        Export
                    </Button>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {['Referrer', 'Contact', ...(mine ? [] : ['Bank']), 'Referrals', 'Rate', 'Sales Value', 'Fee Due', 'Paid', 'Outstanding', 'Borne By'].map(h => (
                                    <TableCell key={h} sx={th}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {byReferrer.map(g => (
                                <TableRow
                                    key={g.referrerId || g.referrerName}
                                    hover
                                    sx={{ cursor: g.referrerId ? 'pointer' : 'default' }}
                                    onClick={() => g.referrerId && navigate(`/reports/referral/${g.referrerId}`)}>
                                    <TableCell sx={{ ...td, fontWeight: 600, color: '#0F172A' }}>{g.referrerName}</TableCell>
                                    <TableCell sx={{ ...td, fontSize: '0.72rem' }}>
                                        <div>{g.phone || '—'}</div>
                                        <div style={{ color: '#94A3B8' }}>{g.email || ''}</div>
                                    </TableCell>
                                    {!mine && (
                                        <TableCell sx={{ ...td, fontSize: '0.72rem' }}>
                                            {(g.bankName || g.bankAccount) ? (
                                                <>
                                                    <div>{g.bankName || '—'}</div>
                                                    <div style={{ color: '#94A3B8' }}>{g.bankAccount || ''}</div>
                                                </>
                                            ) : (
                                                <Chip size="small" label="No bank details"
                                                    sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: '#FEF2F2', color: '#B91C1C' }} />
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell sx={td}>{g.count}</TableCell>
                                    <TableCell sx={td}>{[...g.rates].sort((a, b) => a - b).map(x => `${x}%`).join(' / ') || '—'}</TableCell>
                                    <TableCell sx={td}>{handlePriceWithRM(g.revenue)}</TableCell>
                                    <TableCell sx={{ ...td, fontWeight: 700, color: '#0F172A' }}>{handlePriceWithRM(g.fee)}</TableCell>
                                    <TableCell sx={{ ...td, color: '#166534' }}>{handlePriceWithRM(g.paid)}</TableCell>
                                    <TableCell sx={{ ...td, color: g.outstanding > 0 ? '#B45309' : '#374151', fontWeight: g.outstanding > 0 ? 700 : 400 }}>
                                        {handlePriceWithRM(g.outstanding)}
                                    </TableCell>
                                    <TableCell sx={td}>
                                        {g.astern > 0 && <Chip size="small" label={`Astern ${g.astern}`} sx={{ height: 20, mr: 0.5, fontSize: '0.64rem', fontWeight: 700, bgcolor: '#EFF6FF', color: '#0E8FD1' }} />}
                                        {g.agent > 0 && <Chip size="small" label={`Agent ${g.agent}`} sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: '#FEF3C7', color: '#92400E' }} />}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {byReferrer.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={mine ? 9 : 10} sx={{ textAlign: 'center', py: 3, fontFamily: FONT, fontSize: '0.85rem', color: '#94A3B8' }}>
                                        No referrals in this period.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </div>
    );
}

// ── 2. One referrer's individual report ────────────────────────────────────────
export function ReferralReferrerDetail() {
    const { referrerId } = useParams();
    const navigate = useNavigate();
    const { rows, loading, failed } = useReferralRows();
    const [preset, setPreset] = useState('All Time');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const mine = useMemo(
        () => rows.filter(r => String(r.referrerId) === String(referrerId)),
        [rows, referrerId]);
    const filtered = useMemo(
        () => mine.filter(r => inRange(r, startDate, endDate))
                  .sort((a, b) => new Date(b.depositOn || 0) - new Date(a.depositOn || 0)),
        [mine, startDate, endDate]);

    const who = mine[0];
    const totals = useMemo(() => ({
        count: filtered.length,
        fee: filtered.reduce((s, r) => s + r.fee, 0),
        paid: filtered.reduce((s, r) => s + r.paid, 0),
        outstanding: filtered.reduce((s, r) => s + r.outstanding, 0),
        revenue: filtered.reduce((s, r) => s + r.finalPrice, 0),
    }), [filtered]);

    const monthly = useMemo(() => {
        const m = new Map();
        for (const r of filtered) {
            if (!r.depositOn) continue;
            const k = dayjs(r.depositOn).format('YYYY-MM');
            m.set(k, (m.get(k) || 0) + r.fee);
        }
        return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
            .map(([k, v]) => ({ month: dayjs(k + '-01').format('MMM YY'), fee: Math.round(v) }));
    }, [filtered]);

    const exportMine = () => downloadCsv(
        `Referral_${(who?.referrerName || referrerId).replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}.csv`,
        filtered.map(r => ({
            'Sale ID': r.salesId,
            'Deposit Date': r.depositOn ? convertDateTimeToDateString(r.depositOn) : '',
            Customer: r.customerName ?? '',
            Agent: r.agentName ?? '',
            'Co-Agent': r.coAgentName ?? '',
            Status: r.status ?? '',
            'Final Price': r.finalPrice,
            'Rate %': r.ratePct ?? '',
            'Fee Due': r.fee,
            'Fee Paid': r.paid,
            Outstanding: r.outstanding,
            'Borne By': r.borneBy,
        })));

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    }

    const back = (
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/reports/referral')}
            sx={{ mb: 2, fontFamily: FONT, textTransform: 'none', color: '#64748B', fontWeight: 500, fontSize: '0.85rem' }}>
            Back to Referral Report
        </Button>
    );

    if (!who) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity={failed ? 'error' : 'warning'} sx={{ fontFamily: FONT }}>
                    {failed ? 'Could not load referral fees.' : `No referrals on file for referrer #${referrerId}.`}
                </Alert>
                <Box sx={{ mt: 2 }}>{back}</Box>
            </Box>
        );
    }

    const noBank = !who.bankName && !who.bankAccount;

    return (
        <div>
            {back}

            <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '14px', mb: 2 }}>
                <CardContent>
                    <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>{who.referrerName}</Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: FONT, mb: 1.5 }}>
                        Referrer #{who.referrerId}
                        {who.referrerIsCustomer ? ' · also a customer of ours' : ''}
                    </Typography>
                    {noBank && (
                        <Alert severity="warning" sx={{ mb: 1.5, fontFamily: FONT, fontSize: '0.8rem', borderRadius: '10px' }}>
                            No bank details on file — nothing here can be paid out until they are keyed on the referrer's profile.
                        </Alert>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                        <Kpi label="Phone" value={who.phone || '—'} />
                        <Kpi label="Email" value={who.email || '—'} />
                        <Kpi label="Bank" value={who.bankName || '—'} sub={who.bankAccount || ''} color={noBank ? '#B91C1C' : '#0F172A'} />
                        <Kpi label="Beneficiary" value={who.bankBeneficiary || '—'} />
                    </Box>
                </CardContent>
            </Card>

            <DateFilter {...{ preset, setPreset, startDate, setStartDate, endDate, setEndDate }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2 }}>
                <Kpi label="Referrals" value={totals.count} />
                <Kpi label="Sales Value" value={handlePriceWithRM(totals.revenue)} />
                <Kpi label="Fee Due" value={handlePriceWithRM(totals.fee)} />
                <Kpi label="Paid" value={handlePriceWithRM(totals.paid)} color="#166534" />
                <Kpi label="Outstanding" value={handlePriceWithRM(totals.outstanding)} color={totals.outstanding > 0 ? '#B45309' : '#0F172A'} />
            </Box>

            {monthly.length > 0 && (
                <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '14px', mb: 2 }}>
                    <CardContent>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, fontFamily: FONT, mb: 1 }}>Fees by month</Typography>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={monthly}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: FONT }} />
                                <YAxis tick={{ fontSize: 11, fontFamily: FONT }} tickFormatter={formatCompactRm} />
                                <Tooltip formatter={(v) => handlePriceWithRM(v)} />
                                <Bar dataKey="fee" fill="#0E8FD1" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            )}

            <Card elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '14px' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, fontFamily: FONT }}>Every referral</Typography>
                    <Button size="small" startIcon={<DownloadIcon size={14} />} onClick={exportMine}
                        sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
                        Export
                    </Button>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {['Sale', 'Deposit Date', 'Customer', 'Agent', 'Status', 'Final Price', 'Rate', 'Fee Due', 'Paid', 'Outstanding', 'Borne By', 'Payout'].map(h => (
                                    <TableCell key={h} sx={th}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map(r => (
                                <TableRow key={r.salesId} hover sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/accounting/referral/${r.salesId}`)}>
                                    <TableCell sx={{ ...td, fontWeight: 600, color: '#1A8CFF' }}>#{r.salesId}</TableCell>
                                    <TableCell sx={td}>{r.depositOn ? convertDateTimeToDateString(r.depositOn) : '—'}</TableCell>
                                    <TableCell sx={td}>{r.customerName || '—'}</TableCell>
                                    <TableCell sx={td}>{[r.agentName, r.coAgentName].filter(Boolean).join(' + ') || '—'}</TableCell>
                                    <TableCell sx={td}>{r.status || '—'}</TableCell>
                                    <TableCell sx={td}>{handlePriceWithRM(r.finalPrice)}</TableCell>
                                    <TableCell sx={td}>{r.ratePct != null ? `${Number(r.ratePct)}%` : '—'}</TableCell>
                                    <TableCell sx={{ ...td, fontWeight: 700, color: '#0F172A' }}>{handlePriceWithRM(r.fee)}</TableCell>
                                    <TableCell sx={{ ...td, color: '#166534' }}>{handlePriceWithRM(r.paid)}</TableCell>
                                    <TableCell sx={{ ...td, color: r.outstanding > 0 ? '#B45309' : '#374151' }}>{handlePriceWithRM(r.outstanding)}</TableCell>
                                    <TableCell sx={td}>
                                        <Chip size="small" label={r.borneBy}
                                            sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: r.borneBy === 'Agent' ? '#FEF3C7' : '#EFF6FF', color: r.borneBy === 'Agent' ? '#92400E' : '#0E8FD1' }} />
                                    </TableCell>
                                    <TableCell sx={td}><PayChip fee={r.fee} paid={r.paid} /></TableCell>
                                </TableRow>
                            ))}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={12} sx={{ textAlign: 'center', py: 3, fontFamily: FONT, fontSize: '0.85rem', color: '#94A3B8' }}>
                                        No referrals in this period.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </div>
    );
}
